import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { http } from "../lib/http";
import PageChrome from "../components/shell/PageChrome";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Copy, RefreshCcw, CalendarDays, Mail, FolderLock,
  FileSignature, Bell, Upload, Trash2, Download, CheckCircle2,
  Activity, FileDown,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

export default function ProfilePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [icalUrl, setIcalUrl] = useState("");
  const [loadingIcal, setLoadingIcal] = useState(false);

  const generate = async () => {
    setLoadingIcal(true);
    try {
      const { data } = await http.post("/auth/me/ical-token");
      const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      setIcalUrl(`${base}${data.url_path}`);
      toast.success(t("profile.tokenGenerated"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoadingIcal(false); }
  };
  const regenerate = async () => {
    if (!window.confirm(t("profile.regenerateConfirm"))) return;
    await generate();
  };
  const copy = () => { navigator.clipboard.writeText(icalUrl); toast.success(t("profile.copied")); };

  return (
    <PageChrome title={t("profile.title")} subtitle={t("profile.subtitle")} testId="profile-page">
      <div className="max-w-3xl space-y-6">
        {/* Account */}
        <Card className="bg-white p-6 space-y-3 border border-slate-200" data-testid="profile-account-section">
          <h3 className="font-serif text-xl text-[#0A1F44]" style={{ fontFamily: "'Segoe UI', 'Selawik', Tahoma, Geneva, Verdana, system-ui, sans-serif" }}>
            {t("profile.account")}
          </h3>
          <div className="grid gap-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">{t("profile.fullName")}</span><span className="font-medium">{user?.full_name || "—"}</span></div>
            <div className="flex justify-between"><span className="text-slate-500"><Mail className="h-3.5 w-3.5 inline mr-1"/>{t("profile.email")}</span><span className="font-medium">{user?.email}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{t("profile.role")}</span><Badge variant="secondary">{user?.role}</Badge></div>
          </div>
        </Card>

        {/* Vault */}
        <VaultSection t={t}/>

        {/* Signatures */}
        <SignaturesSection t={t}/>

        {/* Notification prefs */}
        <PrefsSection t={t}/>

        {/* GDPR — My audit log */}
        <MyAuditSection t={t}/>

        {/* GDPR — Data export */}
        <MyDataExportSection t={t}/>

        {/* iCal */}
        <Card className="bg-white p-6 space-y-4 border border-slate-200" data-testid="profile-ical-section">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-[#0A1F44]"/>
            <h3 className="font-serif text-xl text-[#0A1F44]" style={{ fontFamily: "'Segoe UI', 'Selawik', Tahoma, Geneva, Verdana, system-ui, sans-serif" }}>
              {t("profile.icalTitle")}
            </h3>
          </div>
          <p className="text-sm text-slate-600">{t("profile.icalBody")}</p>
          {!icalUrl ? (
            <Button onClick={generate} disabled={loadingIcal} className="bg-[#0A1F44]" data-testid="profile-generate-ical">
              <CalendarDays className="h-4 w-4 mr-2"/>{loadingIcal ? "..." : t("profile.generateLink")}
            </Button>
          ) : (
            <>
              <div className="flex gap-2">
                <Input value={icalUrl} readOnly className="font-mono text-xs bg-slate-50" data-testid="profile-ical-url"/>
                <Button variant="outline" onClick={copy} data-testid="profile-copy-ical"><Copy className="h-4 w-4"/></Button>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={regenerate} data-testid="profile-regenerate-ical"><RefreshCcw className="h-4 w-4 mr-2"/>{t("profile.regenerate")}</Button>
                <span className="text-xs text-slate-500">{t("profile.regenerateWarning")}</span>
              </div>
            </>
          )}
        </Card>
      </div>
    </PageChrome>
  );
}

function VaultSection({ t }) {
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("other");
  const inputRef = useRef(null);
  const load = () => http.get("/me/vault").then(({data}) => setItems(data.items || []));
  useEffect(() => { load(); }, []);
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("kind", kind);
    try {
      await http.post("/me/vault/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success(t("vault.uploaded")); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };
  const onPick = (e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; };
  const del = async (id) => {
    if (!window.confirm(t("vault.deleteConfirm"))) return;
    await http.delete(`/me/vault/${id}`); load(); toast.success(t("common.delete"));
  };
  const download = async (id, filename) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("eata.token");
    const r = await fetch(`${base}/api/me/vault/${id}/download`, { headers: { Authorization: `Bearer ${token}` }});
    if (!r.ok) return toast.error("Download failed");
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <Card className="bg-white p-6 space-y-3 border border-slate-200" data-testid="profile-vault-section">
      <div className="flex items-center gap-2">
        <FolderLock className="h-5 w-5 text-[#0A1F44]"/>
        <h3 className="font-serif text-xl text-[#0A1F44]" style={{ fontFamily: "'Segoe UI', 'Selawik', Tahoma, Geneva, Verdana, system-ui, sans-serif" }}>
          {t("vault.title")}
        </h3>
      </div>
      <p className="text-sm text-slate-600">{t("vault.subtitle")}</p>
      <div className="flex flex-wrap gap-2 items-end">
        <select value={kind} onChange={(e)=>setKind(e.target.value)} className="border rounded px-2 py-1.5 text-sm bg-white" data-testid="vault-kind">
          {["other","identity_doc","contract","medical","certificate","photo"].map(k => <option key={k} value={k}>{t(`vault.kind.${k}`, k)}</option>)}
        </select>
        <input ref={inputRef} type="file" hidden onChange={onPick} data-testid="vault-file-input"/>
        <Button onClick={() => inputRef.current?.click()} className="bg-[#0A1F44]" data-testid="vault-upload-btn">
          <Upload className="h-4 w-4 mr-1"/>{t("vault.upload")}
        </Button>
      </div>
      <div className="border rounded divide-y" data-testid="vault-list">
        {items.length === 0 && <div className="p-4 text-center text-slate-400 text-sm">{t("vault.empty")}</div>}
        {items.map(f => (
          <div key={f.id} className="flex items-center justify-between p-2 hover:bg-slate-50" data-testid={`vault-item-${f.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{f.filename}</div>
              <div className="text-xs text-slate-500">{(f.size/1024).toFixed(1)} KB · {f.kind} · {f.uploaded_at?.slice(0,10)}</div>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={()=>download(f.id, f.filename)} data-testid={`vault-download-${f.id}`}><Download className="h-4 w-4"/></Button>
              <Button size="sm" variant="ghost" onClick={()=>del(f.id)} data-testid={`vault-delete-${f.id}`}><Trash2 className="h-4 w-4 text-rose-600"/></Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function SignaturesSection({ t }) {
  const [items, setItems] = useState([]);
  const load = () => http.get("/signatures").then(({data}) => setItems(data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const downloadSigned = async (sid) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("eata.token");
    const r = await fetch(`${base}/api/signatures/${sid}/signed-pdf`, { headers: { Authorization: `Bearer ${token}` }});
    if (!r.ok) return toast.error("Download failed");
    const blob = await r.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };
  const pending = items.filter(s => s.status === "pending");
  const history = items.filter(s => s.status !== "pending");
  return (
    <Card className="bg-white p-6 space-y-3 border border-slate-200" data-testid="profile-signatures-section">
      <div className="flex items-center gap-2">
        <FileSignature className="h-5 w-5 text-[#0A1F44]"/>
        <h3 className="font-serif text-xl text-[#0A1F44]" style={{ fontFamily: "'Segoe UI', 'Selawik', Tahoma, Geneva, Verdana, system-ui, sans-serif" }}>
          {t("signatures.title")}
        </h3>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{t("signatures.pending")} ({pending.length})</div>
          <div className="space-y-1" data-testid="sig-pending-list">
            {pending.length === 0 && <div className="text-sm text-slate-400">{t("signatures.noPending")}</div>}
            {pending.map(s => (
              <div key={s.id} className="border rounded p-2 text-sm" data-testid={`sig-pending-${s.id}`}>
                <div className="font-medium">{s.document_kind}</div>
                <div className="text-xs text-slate-500">{(s.created_at || "").slice(0,10)}</div>
                {s.token && (
                  <a className="text-xs text-[#C8102E] underline" href={`/sign/${s.id}?token=${s.token}`}>{t("signatures.openLink")}</a>
                )}
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{t("signatures.history")} ({history.length})</div>
          <div className="space-y-1" data-testid="sig-history-list">
            {history.length === 0 && <div className="text-sm text-slate-400">{t("signatures.noHistory")}</div>}
            {history.map(s => (
              <div key={s.id} className="border rounded p-2 text-sm flex items-center justify-between" data-testid={`sig-history-${s.id}`}>
                <div>
                  <div className="font-medium flex items-center gap-1">
                    {s.status === "signed" && <CheckCircle2 className="h-3 w-3 text-emerald-600"/>}
                    {s.document_kind}
                  </div>
                  <div className="text-xs text-slate-500">{s.status} · {(s.signed_at || s.created_at || "").slice(0,10)}</div>
                </div>
                {s.status === "signed" && (
                  <Button size="sm" variant="ghost" onClick={()=>downloadSigned(s.id)} data-testid={`sig-download-${s.id}`}><Download className="h-4 w-4"/></Button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

function PrefsSection({ t }) {
  const [channels, setChannels] = useState({ email: true, sms: true, whatsapp: true, in_app: true });
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    http.get("/me/prefs").then(({data}) => {
      setChannels({ email: true, sms: true, whatsapp: true, in_app: true, ...(data.channels || {})});
    }).finally(() => setLoaded(true));
  }, []);
  const save = async (next) => {
    setChannels(next);
    try { await http.put("/me/prefs", { channels: next }); toast.success(t("prefs.saved")); }
    catch { toast.error("Save failed"); }
  };
  return (
    <Card className="bg-white p-6 space-y-3 border border-slate-200" data-testid="profile-prefs-section">
      <div className="flex items-center gap-2">
        <Bell className="h-5 w-5 text-[#0A1F44]"/>
        <h3 className="font-serif text-xl text-[#0A1F44]" style={{ fontFamily: "'Segoe UI', 'Selawik', Tahoma, Geneva, Verdana, system-ui, sans-serif" }}>
          {t("prefs.title")}
        </h3>
      </div>
      <p className="text-sm text-slate-600">{t("prefs.subtitle")}</p>
      {!loaded ? <div className="text-slate-400 text-sm">{t("common.loading")}</div> : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {["in_app","email","sms","whatsapp"].map(ch => (
            <label key={ch} className="flex items-center gap-2 border rounded p-2 cursor-pointer hover:bg-slate-50">
              <Checkbox checked={channels[ch] !== false} onCheckedChange={(v)=>save({ ...channels, [ch]: !!v })} data-testid={`prefs-${ch}`}/>
              <span className="text-sm capitalize">{ch.replace("_"," ")}</span>
            </label>
          ))}
        </div>
      )}
      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
        {t("prefs.criticalNote")}
      </div>
    </Card>
  );
}

function MyAuditSection({ t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    http.get("/me/audit-of-me?limit=50")
      .then(({data}) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);
  return (
    <Card className="bg-white p-6 space-y-3 border border-slate-200" data-testid="profile-myaudit-section">
      <div className="flex items-center gap-2">
        <Activity className="h-5 w-5 text-[#0A1F44]"/>
        <h3 className="font-serif text-xl text-[#0A1F44]" style={{ fontFamily: "'Segoe UI', 'Selawik', Tahoma, Geneva, Verdana, system-ui, sans-serif" }}>
          {t("audit.title", "My audit log")}
        </h3>
      </div>
      <p className="text-sm text-slate-600">
        {t("audit.subtitle", "Recent GDPR-relevant actions touching your data.")}
      </p>
      {loading ? <div className="text-slate-400 text-sm">{t("common.loading", "Loading…")}</div> : (
        <div className="max-h-80 overflow-y-auto border rounded divide-y" data-testid="myaudit-list">
          {items.length === 0 && <div className="p-3 text-sm text-slate-400 text-center">No entries.</div>}
          {items.map((a, i) => (
            <div key={i} className="p-2 flex items-center gap-3 text-sm hover:bg-slate-50">
              <span className="font-mono text-xs text-slate-500 w-40 flex-shrink-0">
                {a.ts?.slice(0, 19).replace("T", " ")}
              </span>
              <Badge variant="secondary" className="text-xs">{a.action}</Badge>
              <span className="text-slate-700 truncate">{a.summary}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function MyDataExportSection({ t }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const token = localStorage.getItem("eata.token");
      const r = await fetch(`${base}/api/me/data-export`,
        { headers: { Authorization: `Bearer ${token}` }});
      if (!r.ok) { toast.error("Export failed"); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = "my-data.zip"; a.click();
      URL.revokeObjectURL(url);
      toast.success(t("dataExport.ready", "Data ZIP downloaded"));
    } finally { setBusy(false); }
  };
  return (
    <Card className="bg-white p-6 space-y-3 border border-slate-200" data-testid="profile-data-export-section">
      <div className="flex items-center gap-2">
        <FileDown className="h-5 w-5 text-[#0A1F44]"/>
        <h3 className="font-serif text-xl text-[#0A1F44]" style={{ fontFamily: "'Segoe UI', 'Selawik', Tahoma, Geneva, Verdana, system-ui, sans-serif" }}>
          {t("dataExport.title", "Download my data")}
        </h3>
      </div>
      <p className="text-sm text-slate-600">
        {t("dataExport.body", "Under GDPR you have the right to obtain a copy of all personal data we hold about you. The ZIP contains JSON of every linked record plus a branded cover PDF.")}
      </p>
      <Button onClick={download} disabled={busy} className="bg-[#0A1F44]" data-testid="profile-data-export-btn">
        <FileDown className="h-4 w-4 mr-2"/>
        {busy ? t("common.loading", "Preparing…") : t("dataExport.button", "Download my data (ZIP)")}
      </Button>
    </Card>
  );
}

